<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
	IonContent,
	IonButton,
	IonIcon,
	IonSpinner,
	IonRefresher,
	IonRefresherContent,
	RefresherCustomEvent,
} from '@ionic/vue';
import {
	chevronBack,
	share,
	downloadOutline,
} from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import QueueTicketItem from '@/modules/queue/components/QueueTicketItem.vue';
import useQueue from '@/modules/queue/composables/useQueue';

// Props
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const slotId = ref(parseInt(route.params.queueSlotId as string));

// Queue composables
const { getMyQueues } = useQueue();
const { myQueues, isFetchingMyQueues, refetchMyQueues } = getMyQueues();

// Find the current slot and all slots for the same activity
const currentSlot = computed(() => {
	return myQueues.value?.find(s => s.id === slotId.value);
});

// Get the activity ID to group tickets
const activityId = computed(() => {
	if (!currentSlot.value) return null;
	// Try queue_activity.id first, then fall back to sub_activity.queue_activity_id
	return currentSlot.value.queue_activity?.id || currentSlot.value.sub_activity?.queue_activity_id || null;
});

// Get the booked date of current slot
const bookedDate = computed(() => {
	return currentSlot.value?.booked_date;
});

// Get all slots for this activity and same date
const activitySlots = computed(() => {
	if (!activityId.value || !myQueues.value || !bookedDate.value) return [];

	return myQueues.value.filter(s => {
		// Get activity ID from slot (try queue_activity.id first, then sub_activity.queue_activity_id)
		const slotActivityId = s.queue_activity?.id || s.sub_activity?.queue_activity_id;
		return slotActivityId === activityId.value &&
		       s.booked_date === bookedDate.value &&
		       ['active', 'waiting', 'called', 'in_progress', 'completed'].includes(s.status);
	}).sort((a, b) => a.position_number - b.position_number);
});

// Current ticket index
const currentTicketIndex = computed(() => {
	const index = activitySlots.value.findIndex(s => s.id === slotId.value);
	return index >= 0 ? index : 0;
});

// Ticket count
const ticketCount = computed(() => activitySlots.value.length);

// Layout helpers
const isSingleTicket = computed(() => ticketCount.value === 1);

// Activity name for header
const activityName = computed(() => {
	return currentSlot.value?.queue_activity?.name || t('queue.queue_ticket');
});

// Header title with count
const headerTitle = computed(() => {
	if (ticketCount.value > 1) {
		return `${activityName.value} (${currentTicketIndex.value + 1}/${ticketCount.value})`;
	}
	return activityName.value;
});

// Auto-refresh interval
const refreshInterval = ref<NodeJS.Timeout | null>(null);
const autoRefreshEnabled = ref(true);

// Pull to refresh
const handleRefresh = async (event: RefresherCustomEvent) => {
	await refetchMyQueues();
	event.target.complete();
};

// Start auto-refresh
const startAutoRefresh = () => {
	if (refreshInterval.value) return;

	refreshInterval.value = setInterval(async () => {
		if (autoRefreshEnabled.value && document.visibilityState === 'visible') {
			await refetchMyQueues();
		}
	}, 15000); // Refresh every 15 seconds
};

// Stop auto-refresh
const stopAutoRefresh = () => {
	if (refreshInterval.value) {
		clearInterval(refreshInterval.value);
		refreshInterval.value = null;
	}
};

// Share ticket
const shareTicket = async () => {
	if (navigator.share && currentSlot.value) {
		try {
			await navigator.share({
				title: t('queue.my_queue_ticket'),
				text: t('queue.queue_ticket_for', {
					activity: currentSlot.value.queue_activity?.name
				}),
				url: window.location.href,
			});
		} catch (error) {
			console.log('Error sharing:', error);
		}
	}
};

// Download ticket (placeholder - implement based on platform)
const downloadTicket = async () => {
	const alert = await alertController.create({
		header: t('queue.download_ticket'),
		message: t('queue.download_ticket_message'),
		buttons: [t('general.ok')],
	});
	await alert.present();
};

// Buy fastpass - navigate to upsell checkout
const buyFastpass = async (slotIdToUpsell: number) => {
	// Find the slot and its activity
	const slotToUpgrade = myQueues.value?.find(s => s.id === slotIdToUpsell);
	if (!slotToUpgrade) {
		return;
	}

	const activity = slotToUpgrade.queue_activity;
	if (!activity || !activity.upsell_price || !activity.upsellCurrency) {
		return;
	}

	// Navigate to upsell checkout page
	await router.push({
		path: `/queue/upsell/${slotIdToUpsell}`,
		query: {
			activityId: activity.id.toString()
		}
	});
};

// Lifecycle
onMounted(async () => {
	startAutoRefresh();
	// If slot not found initially, refetch to ensure we have the latest data
	if (!currentSlot.value && !isFetchingMyQueues.value) {
		await refetchMyQueues();
	}
});

onUnmounted(() => {
	stopAutoRefresh();
});

// Handle visibility change
document.addEventListener('visibilitychange', () => {
	if (document.visibilityState === 'visible' && autoRefreshEnabled.value) {
		refetchMyQueues();
	}
});
</script>

<template>
	<ion-content class="queue-ticket-content">
		<ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
			<ion-refresher-content></ion-refresher-content>
		</ion-refresher>

		<!-- Loading state -->
		<div v-if="isFetchingMyQueues && !activitySlots.length" class="loading-container">
			<ion-spinner name="dots" class="loading-spinner"></ion-spinner>
		</div>

		<!-- Main Content -->
		<div v-else-if="activitySlots.length > 0">
			<!-- Header with back button -->
			<div class="header-section">
				<div class="flex items-center px-6 pt-16 pb-6">
					<IonButton
						fill="clear"
						color="light"
						@click="router.push('/my-queues')"
						class="back-button">
						<IonIcon :icon="chevronBack" class="back-icon" />
					</IonButton>
					<h1 class="header-title">{{ headerTitle }}</h1>
				</div>
			</div>

			<!-- Tickets container -->
			<div class="tickets-container">
				<!-- Tickets scroll container -->
				<div class="tickets-scroll-container">
					<div class="tickets-scroll-inner" :class="{ 'single-ticket': isSingleTicket }">
						<QueueTicketItem
							v-for="slot in activitySlots"
							:key="slot.id"
							:slot="slot"
							:wallet-linked="!!slot.user?.wallet_id"
							:show-disclaimer="true"
							class="ticket-item"
							@buy-fastpass="buyFastpass"
						/>
					</div>
				</div>

				<!-- Bottom buttons -->
				<!-- TODO: Uncomment when download and share functionality is implemented -->
				<!-- <div class="bottom-buttons">
					<ion-button
						class="download-button"
						@click="downloadTicket">
						<ion-icon :icon="downloadOutline" slot="start"></ion-icon>
						{{ t('queue.download') }}
					</ion-button>
					<ion-button
						class="share-button"
						fill="outline"
						@click="shareTicket">
						<ion-icon :icon="share" slot="start"></ion-icon>
						{{ t('queue.share') }}
					</ion-button>
				</div> -->
			</div>
		</div>

		<!-- Error/Empty state -->
		<div v-else class="error-container">
			<p class="error-text">{{ t('queue.ticket_not_found') }}</p>
			<p class="error-subtext">{{ t('queue.ticket_not_found_description') }}</p>
			<ion-button fill="outline" @click="router.push('/my-queues')" class="back-to-queues-button">
				{{ t('queue.back_to_my_queues') }}
			</ion-button>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped>
// Content
.queue-ticket-content {
	--background: #0a0a0a;
}

// Header Section
.header-section {
	background: #0a0a0a;

	.back-button {
		--color: #fff;
		--padding-start: 0;
		--padding-end: 0;
		width: 40px;
		height: 40px;
		margin-right: 16px;
	}

	.back-icon {
		font-size: 24px;
		color: #fff;
	}

	.header-title {
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 20px;
		font-weight: 600;
		color: #fff;
		margin: 0;
	}
}

// Loading
.loading-container {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	min-height: 400px;
}

.loading-spinner {
	--color: white;
	width: 48px;
	height: 48px;
}

// Tickets container
.tickets-container {
	position: relative;
	min-height: 100vh;
	padding-top: 24px;
	padding-bottom: 140px;
}

// Background - removed, using consistent dark background

// Tickets scroll
.tickets-scroll-container {
	position: relative;
	width: 100%;
	overflow-x: auto;
	overflow-y: hidden;
	padding: 0 20px;
	margin-bottom: 24px;
	scroll-snap-type: x mandatory;
	-webkit-overflow-scrolling: touch;

	&::-webkit-scrollbar {
		display: none;
	}
	scrollbar-width: none;
}

.tickets-scroll-inner {
	display: flex;
	align-items: stretch;
	gap: 16px;
	width: max-content;
	padding: 4px; // Slight padding for shadow
}

.tickets-scroll-inner.single-ticket {
	margin: 0 auto;
}

.ticket-item {
	scroll-snap-align: center;
	flex-shrink: 0;
}

// Bottom buttons
.bottom-buttons {
	padding: 0 20px 40px;
	display: flex;
	gap: 12px;
}

.download-button {
	--background: white;
	--background-hover: rgba(255, 255, 255, 0.9);
	--background-activated: rgba(255, 255, 255, 0.8);
	--color: #0a0a0a;
	--border-radius: 25px;
	flex: 1;
	height: 48px;
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 16px;
	text-transform: none;

	ion-icon {
		font-size: 20px;
	}
}

.share-button {
	--background: transparent;
	--background-hover: rgba(255, 255, 255, 0.1);
	--background-activated: rgba(255, 255, 255, 0.05);
	--color: white;
	--border-color: rgba(255, 255, 255, 0.3);
	--border-width: 2px;
	--border-radius: 25px;
	flex: 1;
	height: 48px;
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 16px;
	text-transform: none;

	ion-icon {
		font-size: 20px;
	}
}

// Error state
.error-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 400px;
	padding: 40px;
	text-align: center;
}

.error-text {
	color: white;
	font-size: 20px;
	font-weight: 600;
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	margin: 0 0 8px 0;
}

.error-subtext {
	color: rgba(255, 255, 255, 0.6);
	font-size: 16px;
	font-family: 'SourceSansPro-Regular', sans-serif;
	margin: 0 0 24px 0;
}

.back-to-queues-button {
	--border-width: 2px;
	--border-color: rgba(255, 255, 255, 0.3);
	--border-radius: 25px;
	--background: transparent;
	--color: white;
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 16px;
	height: 48px;
	min-width: 200px;
}
</style>
